import { type Editor, useCurrentEditor } from '@tiptap/react';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  Dialog,
  IconLink,
  Inline,
  TextField,
} from 'braid-design-system';
import { useCallback, useState } from 'react';

import translations from './.vocab';
import { MenuButton } from './MenuButton';

const AddLinkDialog = ({
  previousUrl = '',
  editor,
  closeDialog,
}: {
  previousUrl: string;
  editor: Editor;
  closeDialog: () => void;
}) => {
  const [url, setUrl] = useState(previousUrl);
  const { t } = useTranslations(translations);

  const getWordPosition = (): { from: number; to: number } => {
    const { state } = editor.view;
    const { from } = state.selection;
    let startPos = from;
    let endPos = from;

    // Use a regex that includes more word characters (like hyphens, apostrophes)
    const wordCharRegex = /[\w\u00C0-\u017F\-']/;

    // Move backward to find start of word
    while (
      startPos > 0 &&
      wordCharRegex.test(state.doc.textBetween(startPos - 1, startPos))
    ) {
      startPos--;
    }

    // Move forward to find end of word
    const docSize = state.doc.content.size;
    while (
      endPos < docSize &&
      wordCharRegex.test(state.doc.textBetween(endPos, endPos + 1))
    ) {
      endPos++;
    }

    return { from: startPos, to: endPos };
  };

  return (
    <Dialog
      open={true}
      title={t('Add link')}
      onClose={() => {
        closeDialog();
      }}
      id="add-link"
    >
      <Inline space="xxsmall" alignY="bottom">
        <Box flexGrow={1}>
          <TextField
            label={t('URL')}
            value={url}
            onChange={(e) => setUrl(e.currentTarget.value)}
            id="add-link-url"
            inputMode="url"
            placeholder="http://example.com"
          />
        </Box>
        <Button
          tone="formAccent"
          onClick={(e) => {
            e.preventDefault();
            const { from, to } = getWordPosition();
            editor
              .chain()
              .focus()
              .extendMarkRange('link')
              .setTextSelection({ from, to })
              .setLink({ href: url })
              .run();
            closeDialog();
          }}
        >
          {t('Add')}
        </Button>
      </Inline>
    </Dialog>
  );
};

export const LinkSetButton = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const { editor } = useCurrentEditor();

  const setLink = useCallback(() => {
    if (!editor) {
      return null;
    }

    // Check if Link extension is enabled
    if (!editor.can().extendMarkRange('link')) {
      return null;
    }

    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }

    setOpenDialog(true);
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <>
      <MenuButton isActive={editor.isActive('link')} onClick={setLink}>
        <IconLink />
      </MenuButton>
      {openDialog && (
        <AddLinkDialog
          previousUrl={editor.getAttributes('link').href}
          closeDialog={() => setOpenDialog(false)}
          editor={editor}
        />
      )}
    </>
  );
};
